// src/pages/DebtStudio/components/CostOfIssuance.jsx
import React, { useState } from "react";
import SectionCard from "components/SectionCard";
import { TextInput, NumberInput, DropdownSelect } from "components/InputStyled";
import { GripVertical, Trash2 } from "lucide-react";

const COST_TYPES = [
  "Cost of Issuance (Total)",
  "Underwriter's Discount",
  "Legal Counsel",
  "Lender",
  "Financial Advisor",
  "Conduit Issuer",
  "Credit Enhancement",
  "Rating Agency",
  "Trustee/Paying Agent",
  "Printing/Disclosure",
  "Miscellaneous",
];

const RATE_TYPES = [
  { value: "fixed", label: "Fixed ($)" },
  { value: "per1000", label: "$ per $1,000" },
  { value: "percent", label: "Percent (%)" },
];

function defaultYieldFlagsForType(type) {
  const t = String(type || "").trim().toLowerCase();

  if (t === "underwriter's discount") {
    return {
      includeInTIC: true,
      includeInAIC: true,
      includeInArbYield: false,
    };
  }

  if (t === "credit enhancement") {
    return {
      includeInTIC: false,
      includeInAIC: true,
      includeInArbYield: true,
    };
  }

  if (t) {
    return {
      includeInTIC: false,
      includeInAIC: true,
      includeInArbYield: false,
    };
  }

  return {
    includeInTIC: false,
    includeInAIC: false,
    includeInArbYield: false,
  };
}

function newCostRow(type = "") {
  return {
    type,
    firm: "",
    rateType: "fixed",
    amount: "",
    ...defaultYieldFlagsForType(type),
    yieldFlagsMode: "default",
  };
}

function withYieldDefaults(row = {}) {
  const defaults = defaultYieldFlagsForType(row.type);

  return {
    ...row,
    includeInTIC:
      typeof row.includeInTIC === "boolean"
        ? row.includeInTIC
        : defaults.includeInTIC,
    includeInAIC:
      typeof row.includeInAIC === "boolean"
        ? row.includeInAIC
        : defaults.includeInAIC,
    includeInArbYield:
      typeof row.includeInArbYield === "boolean"
        ? row.includeInArbYield
        : defaults.includeInArbYield,
    yieldFlagsMode:
      row.yieldFlagsMode === "custom" ? "custom" : "default",
  };
}

function HeaderRow({ columns }) {
  return (
    <div className="hidden md:grid grid-cols-[0.55fr_1.6fr_1.8fr_1.1fr_1fr_1.3fr_0.4fr] gap-3 mb-2 text-xs text-muted">
      <div>Order</div>
      {columns.map((c, i) => (
        <div key={i}>{c}</div>
      ))}
      <div />
    </div>
  );
}

function AmountCell({ value, rateType, onChange, disabled }) {
  return (
    <NumberInput
      value={value ?? ""}
      onChange={onChange}
      type={rateType === "percent" ? "percent" : "decimal"}
      className="w-full text-right"
      disabled={disabled}
    />
  );
}

function YieldCheckbox({
  label,
  checked,
  onChange,
  disabled,
  isDefault = false,
}) {
  const labelCls =
    "inline-flex items-center gap-1 text-xs text-app";

  const inputCls = isDefault
    ? "h-4 w-4 opacity-40"
    : "h-4 w-4";

  return (
    <label className={labelCls}>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className={inputCls}
        title={isDefault ? "Default inclusion setting" : "Custom inclusion setting"}
      />
      <span>{label}</span>
    </label>
  );
}

export default function CostOfIssuance({
  isReadOnly = false,
  costOfIssuance = [],
  additionalSources = [],
  onChangeCostOfIssuance,
  onChangeAdditionalSources,
}) {
  const [draggedCostIndex, setDraggedCostIndex] = useState(null);

  const coiRows =
    costOfIssuance.length > 0
      ? costOfIssuance.map(withYieldDefaults)
      : [newCostRow()];

  const updateCost = (idx, key, val) => {
    if (isReadOnly) return;

    // IMPORTANT:
    // Always update from normalized rows so the persisted
    // state matches the displayed default checkbox values.
    const base = coiRows;

    onChangeCostOfIssuance &&
      onChangeCostOfIssuance(
        base.map((r, i) => {
          if (i !== idx) return r;

          if (key === "type") {
            return {
              ...r,
              type: val,
              ...defaultYieldFlagsForType(val),
              yieldFlagsMode: "default",
            };
          }

          if (
            key === "includeInTIC" ||
            key === "includeInAIC" ||
            key === "includeInArbYield"
          ) {
            return {
              ...r,
              [key]: val,
              yieldFlagsMode: "custom",
            };
          }

          return { ...r, [key]: val };
        })
      );
  };

  const addCost = () => {
    if (isReadOnly) return;

    // Preserve normalized/defaulted rows when adding
    // a new row so default flags persist correctly.
    onChangeCostOfIssuance &&
      onChangeCostOfIssuance([...coiRows, newCostRow()]);
  };

  const moveCost = (fromIndex, toIndex) => {
    if (
      isReadOnly ||
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= coiRows.length ||
      toIndex >= coiRows.length
    ) {
      return;
    }

    const reordered = [...coiRows];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    onChangeCostOfIssuance && onChangeCostOfIssuance(reordered);
  };

  const removeCost = (idx) => {
    if (isReadOnly) return;

    onChangeCostOfIssuance &&
      onChangeCostOfIssuance(
        coiRows.filter((_, i) => i !== idx)
      );
  };

  const updateSource = (idx, key, val) => {
    if (isReadOnly) return;

    onChangeAdditionalSources &&
      onChangeAdditionalSources(
        additionalSources.map((r, i) => (i === idx ? { ...r, [key]: val } : r))
      );
  };

  const addSource = () => {
    if (isReadOnly) return;

    onChangeAdditionalSources &&
      onChangeAdditionalSources([
        ...additionalSources,
        { source: "", description: "", amount: "" },
      ]);
  };

  const removeSource = (idx) => {
    if (isReadOnly) return;

    onChangeAdditionalSources &&
      onChangeAdditionalSources(additionalSources.filter((_, i) => i !== idx));
  };

  return (
    <>
      <SectionCard title="Cost of Issuance">
        <HeaderRow
          columns={[
            "Type",
            "Description",
            "Rate Type",
            "Amount ($) / Rate",
            "Include In Yield Metric",
          ]}
        />

        <div className="space-y-2">
          {coiRows.map((cost, idx) => {
            const isDefaultYieldFlags = cost.yieldFlagsMode !== "custom";

            return (
              <div
                key={idx}
                className={`grid grid-cols-1 md:grid-cols-[0.55fr_1.6fr_1.8fr_1.1fr_1fr_1.3fr_0.4fr] gap-3 items-center ${
                  draggedCostIndex === idx ? "opacity-50" : ""
                }`}
                onDragOver={(e) => {
                  if (!isReadOnly && draggedCostIndex !== null) e.preventDefault();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (draggedCostIndex !== null) moveCost(draggedCostIndex, idx);
                  setDraggedCostIndex(null);
                }}
              >
                <div className="flex items-center">
                  <button
                    type="button"
                    draggable={!isReadOnly}
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/plain", String(idx));
                      setDraggedCostIndex(idx);
                    }}
                    onDragEnd={() => setDraggedCostIndex(null)}
                    className="cursor-grab text-muted hover:text-app active:cursor-grabbing disabled:cursor-default disabled:opacity-40"
                    disabled={isReadOnly}
                    aria-label={`Drag cost item ${idx + 1} to reorder`}
                    title="Drag to reorder"
                  >
                    <GripVertical size={16} aria-hidden="true" />
                  </button>
                </div>

                <DropdownSelect
                  value={cost.type}
                  onChange={(v) => updateCost(idx, "type", v)}
                  options={COST_TYPES.map((t) => ({ value: t, label: t }))}
                  placeholder="None"
                  mutedPlaceholder
                  className="w-full"
                  disabled={isReadOnly}
                />

                <TextInput
                  value={cost.firm || ""}
                  onChange={(v) => updateCost(idx, "firm", v)}
                  placeholder="Description (optional)"
                  className="w-full"
                  disabled={isReadOnly}
                />

                <DropdownSelect
                  value={cost.rateType || "fixed"}
                  onChange={(v) => updateCost(idx, "rateType", v)}
                  options={RATE_TYPES}
                  className="w-full"
                  disabled={isReadOnly}
                />

                <AmountCell
                  value={cost.amount}
                  rateType={cost.rateType}
                  onChange={(n) => updateCost(idx, "amount", n)}
                  disabled={isReadOnly}
                />

                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  <YieldCheckbox
                    label="TIC"
                    checked={cost.includeInTIC}
                    onChange={(v) => updateCost(idx, "includeInTIC", v)}
                    disabled={isReadOnly}
                    isDefault={isDefaultYieldFlags}
                  />
                  <YieldCheckbox
                    label="AIC"
                    checked={cost.includeInAIC}
                    onChange={(v) => updateCost(idx, "includeInAIC", v)}
                    disabled={isReadOnly}
                    isDefault={isDefaultYieldFlags}
                  />
                  <YieldCheckbox
                    label="Arb Yld"
                    checked={cost.includeInArbYield}
                    onChange={(v) => updateCost(idx, "includeInArbYield", v)}
                    disabled={isReadOnly}
                    isDefault={isDefaultYieldFlags}
                  />
                </div>

                <div className="flex justify-end">
                  {isReadOnly ? null : coiRows.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => removeCost(idx)}
                      className="text-red-500 hover:text-red-700"
                      tabIndex={-1}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-5">
          <button
            type="button"
            className="btn btn-primary"
            onClick={addCost}
            disabled={isReadOnly}
            title={isReadOnly ? "Read-only" : undefined}
          >
            + Add Cost
          </button>
        </div>
      </SectionCard>

      <div className="mt-6">
        <SectionCard title="Additional Sources">
          {additionalSources.length > 0 && (
            <div className="hidden md:grid grid-cols-[2fr_3fr_1fr_0.4fr] gap-3 mb-2 text-xs text-muted">
              <div>Source</div>
              <div>Description</div>
              <div>Amount ($)</div>
              <div />
            </div>
          )}

          <div className="space-y-2">
            {additionalSources.map((src, idx) => (
              <div
                key={idx}
                className="grid grid-cols-1 md:grid-cols-[2fr_3fr_1fr_0.4fr] gap-3 items-center"
              >
                <TextInput
                  value={src.source || ""}
                  onChange={(v) => updateSource(idx, "source", v)}
                  placeholder="Source"
                  className="w-full"
                  disabled={isReadOnly}
                />

                <TextInput
                  value={src.description || ""}
                  onChange={(v) => updateSource(idx, "description", v)}
                  placeholder="Description (optional)"
                  className="w-full"
                  disabled={isReadOnly}
                />

                <NumberInput
                  value={src.amount ?? ""}
                  onChange={(n) => updateSource(idx, "amount", n)}
                  type="decimal"
                  className="w-full text-right"
                  disabled={isReadOnly}
                />

                <div className="flex justify-end">
                  {isReadOnly ? null : (
                    <button
                      type="button"
                      onClick={() => removeSource(idx)}
                      className="text-red-500 hover:text-red-700"
                      tabIndex={-1}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-5">
            <button
              type="button"
              className="btn btn-primary"
              onClick={addSource}
              disabled={isReadOnly}
              title={isReadOnly ? "Read-only" : undefined}
            >
              + Add Source
            </button>
          </div>
        </SectionCard>
      </div>
    </>
  );
}
